import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: 'preaching_style',
    question: 'Tell us about your preaching style. Do you see yourself as more conversational, formal, storytelling-oriented, or something else?',
    placeholder: 'Describe your preaching style...'
  },
  {
    id: 'tone',
    question: 'What kind of tone do you usually aim for in your sermons—warm and encouraging, passionate and fiery, scholarly and detailed, or another tone?',
    placeholder: 'Describe your typical sermon tone...'
  },
  {
    id: 'favorite_preachers',
    question: 'Who are some of your favorite preachers or speakers, and what do you like most about their delivery?',
    placeholder: 'List your favorite preachers and what inspires you about them...'
  },
  {
    id: 'examples_stories',
    question: 'What kind of examples or stories do you like to use when you preach? Personal anecdotes, historical references, modern-day analogies, or biblical narratives?',
    placeholder: 'Describe the types of examples you use...'
  },
  {
    id: 'language_vocabulary',
    question: 'When you think about your congregation or audience, what kind of language or vocabulary do you find yourself using most? Casual and everyday, more formal and theological, or a mix of both?',
    placeholder: 'Describe your language style...'
  },
  {
    id: 'recurring_scriptures',
    question: 'Which scriptures or biblical themes do you find yourself returning to most often when you preach?',
    placeholder: 'List your favorite scriptures or themes...'
  },
  {
    id: 'sermon_length',
    question: 'How long is your ideal sermon? Do you prefer shorter messages around 15-20 minutes, or more in-depth sermons that are 30 minutes or longer?',
    placeholder: 'Describe your ideal sermon length...'
  },
  {
    id: 'desired_impact',
    question: 'What do you hope people feel or think by the end of one of your sermons? Inspired, challenged, comforted, motivated to take action, or something else?',
    placeholder: 'Describe the impact you hope to achieve...'
  },
  {
    id: 'sense_of_humor',
    question: 'How would you describe your sense of humor when you preach? Light and playful, subtle and dry, or do you prefer to keep things more serious?',
    placeholder: 'Describe your use of humor...'
  },
  {
    id: 'passionate_topics',
    question: 'What are some topics you feel most passionate about teaching or preaching on? Social issues, spiritual growth, biblical history, or practical life application?',
    placeholder: 'List topics you are most passionate about...'
  },
  {
    id: 'signature_phrases',
    question: "Do you have any particular phrases, sayings, or expressions you often use that you'd like the AI to incorporate?",
    placeholder: 'List any signature phrases or expressions...'
  },
  {
    id: 'sermon_structure',
    question: 'How do you typically structure your sermons? For example, do you start with a story, use a three-point outline, or follow a different format?',
    placeholder: 'Describe your sermon structure...'
  },
  {
    id: 'illustrations_metaphors',
    question: 'Are there any types of illustrations or metaphors you find yourself using often—like nature imagery, sports analogies, or references to everyday life?',
    placeholder: 'Describe your favorite types of illustrations...'
  },
  {
    id: 'closing_style',
    question: 'What kind of closing or call to action do you like to include at the end of your sermons? An altar call, a prayer, a challenge to reflect, or a next step for the week?',
    placeholder: 'Describe your typical sermon closing...'
  },
  {
    id: 'humor_personal_stories',
    question: 'How do you feel about using humor or personal stories in your sermons? Do you enjoy it, use it sparingly, or prefer to keep things more formal?',
    placeholder: 'Describe your approach to humor and personal stories...'
  },
  {
    id: 'congregation_feedback',
    question: 'What kind of feedback have you received from your congregation or listeners about what they appreciate most in your sermons?',
    placeholder: 'Share feedback you have received...'
  },
  {
    id: 'target_demographic',
    question: 'Is there a particular demographic or age group you tend to speak to, and does that influence how you shape your messages?',
    placeholder: 'Describe your target audience...'
  },
  {
    id: 'sermon_type_preference',
    question: 'Do you prefer your sermons to be more expository (verse-by-verse), topical, narrative-driven, or a mix of different styles?',
    placeholder: 'Describe your preferred sermon type...'
  },
  {
    id: 'favorite_biblical_books',
    question: 'What are some of your favorite biblical books or passages to preach from, and why?',
    placeholder: 'List your favorite books and passages...'
  },
  {
    id: 'additional_context',
    question: "Finally, is there anything else you'd like the AI to know about your personality, your ministry context, or your unique preaching voice?",
    placeholder: 'Share any additional context...'
  }
];

export default function SermonProfileQuestionnaire({ onComplete, onSkip }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState(() => {
    // Try to restore from localStorage backup on mount
    try {
      const backup = localStorage.getItem('sermon_profile_backup');
      if (backup) {
        console.log('Restoring answers from backup');
        return JSON.parse(backup);
      }
    } catch (e) {
      console.warn('Could not restore backup:', e);
    }
    return {};
  });
  const questionsPerPage = 5;
  
  const totalPages = Math.ceil(QUESTIONS.length / questionsPerPage);
  const startIdx = currentPage * questionsPerPage;
  const endIdx = startIdx + questionsPerPage;
  const currentQuestions = QUESTIONS.slice(startIdx, endIdx);

  const handleAnswerChange = (questionId, value) => {
    const updatedAnswers = {
      ...answers,
      [questionId]: value
    };
    setAnswers(updatedAnswers);
    
    // Auto-save to localStorage as user types
    try {
      localStorage.setItem('sermon_profile_progress', JSON.stringify(updatedAnswers));
    } catch (e) {
      console.warn('Could not auto-save progress:', e);
    }
  };

  const isCurrentPageComplete = () => {
    return currentQuestions.every(q => answers[q.id]?.trim().length > 0);
  };

  const handleNext = () => {
    if (!isCurrentPageComplete()) {
      toast.error('Please answer all questions before continuing');
      return;
    }
    
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isCurrentPageComplete()) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setLoading(true);
    
    // Save to localStorage as backup before submitting
    try {
      localStorage.setItem('sermon_profile_backup', JSON.stringify(answers));
      console.log('Backup saved to localStorage');
    } catch (e) {
      console.warn('Could not save backup to localStorage:', e);
    }
    
    let retryCount = 0;
    const maxRetries = 3;
    
    const attemptSave = async () => {
      try {
        console.log(`[Attempt ${retryCount + 1}/${maxRetries}] Submitting sermon profile with ${Object.keys(answers).length} questions`);
        console.log('Answer keys:', Object.keys(answers));
        console.log('API endpoint:', `${API}/users/sermon-profile`);
        
        const response = await axios.post(
          `${API}/users/sermon-profile`, 
          { sermon_profile: answers },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: true,
            timeout: 30000 // 30 second timeout
          }
        );
        
        console.log('✅ Sermon profile saved successfully!', response.data);
        
        // Verify the response contains the saved profile
        if (response.data.user && response.data.user.sermon_profile) {
          console.log('✅ Verified: Profile saved with', Object.keys(response.data.user.sermon_profile).length, 'answers');
          
          // Clear backup after successful save
          try {
            localStorage.removeItem('sermon_profile_backup');
            console.log('Backup cleared from localStorage');
          } catch (e) {
            console.warn('Could not clear backup:', e);
          }
          
          toast.success('Your sermon profile has been saved! This will help personalize all your AI-generated sermons.', {
            duration: 5000
          });
          onComplete(answers);
          return true;
        } else {
          throw new Error('Server response missing sermon profile data');
        }
        
      } catch (error) {
        console.error(`❌ Error on attempt ${retryCount + 1}:`, error);
        console.error('Error type:', error.name);
        console.error('Error message:', error.message);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        retryCount++;
        
        if (retryCount < maxRetries) {
          console.log(`Retrying... (${retryCount}/${maxRetries})`);
          toast.info(`Retrying save... (${retryCount}/${maxRetries})`, { duration: 2000 });
          await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retry
          return attemptSave();
        } else {
          // All retries failed
          const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
          console.error('❌ All retry attempts failed. Error:', errorMessage);
          
          toast.error(
            `Failed to save after ${maxRetries} attempts. Your answers are backed up. Please try again or contact support.`,
            { duration: 8000 }
          );
          
          // Show detailed error in console for debugging
          console.error('Full error object:', JSON.stringify({
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
          }, null, 2));
          
          return false;
        }
      }
    };
    
    try {
      await attemptSave();
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentPage + 1) / totalPages) * 100;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-2 sm:p-4">
        <div className="w-full max-w-3xl my-4 sm:my-8">
          <Card className="border-2 border-pulpit-200">
            <CardHeader className="bg-gradient-to-r from-pulpit-600 to-pulpit-700 text-white rounded-t-lg p-4 sm:p-6">
              <CardTitle className="text-xl sm:text-2xl md:text-3xl font-display">
                PulpitPilot Sermon Profile
              </CardTitle>
              <CardDescription className="text-pulpit-100 text-sm sm:text-base">
                Help us understand your unique preaching voice and style
              </CardDescription>
              
              <div className="mt-3 sm:mt-4">
                <div className="flex justify-between text-xs sm:text-sm mb-2">
                  <span>Page {currentPage + 1} of {totalPages}</span>
                  <span>{Math.round(progress)}% Complete</span>
                </div>
                <div className="w-full bg-pulpit-200/30 rounded-full h-2">
                  <div 
                    className="bg-glory-gold h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </CardHeader>

          <CardContent className="p-4 sm:p-6 space-y-3 sm:space-y-4">
          {currentQuestions.map((q, idx) => (
            <div key={q.id} className="space-y-2">
              <Label className="text-sm sm:text-base font-semibold text-gray-900 leading-tight">
                {startIdx + idx + 1}. {q.question}
              </Label>
              <Textarea
                value={answers[q.id] || ''}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                placeholder={q.placeholder}
                className="min-h-[70px] sm:min-h-[80px] border-2 focus:border-pulpit-500 text-sm sm:text-base"
                required
              />
            </div>
          ))}

          </CardContent>
          
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 p-4 sm:p-6 sm:pt-4 border-t bg-gray-50 rounded-b-lg">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentPage === 0}
              className="border-pulpit-300 w-full sm:w-auto"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <div className="text-xs sm:text-sm text-gray-600 text-center order-first sm:order-none">
              Questions {startIdx + 1}-{Math.min(endIdx, QUESTIONS.length)} of {QUESTIONS.length}
            </div>

            {currentPage < totalPages - 1 ? (
              <Button
                onClick={handleNext}
                disabled={!isCurrentPageComplete()}
                className="bg-pulpit-600 hover:bg-pulpit-700 w-full sm:w-auto"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isCurrentPageComplete() || loading}
                className="bg-glory-600 hover:bg-glory-700 text-white w-full sm:w-auto font-semibold text-base py-6"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving Your Profile...
                  </>
                ) : (
                  '✅ Complete Profile'
                )}
              </Button>
            )}
          </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
